import { useEffect, useState } from 'react'

import styled from '@emotion/styled'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { NavigationCloseIcon } from '@arcade/web/icons/react'
import { Button, Flex, PaddingInset, spacing } from '@cash-design-system/react'
import { SignatureBlocker } from '@cash-web/protos/squareup/franklin/blockers/signature_blocker.pb'
import { useAuth } from '@cash-web/shared-auth-data-access-context'
import { useBlockerResponseHandler } from '@cash-web/shared-blocker-data-access-context'
import { useMutationSetSignature } from '@cash-web/shared-data-access-api/react-query/useMutationSetSignature'
import { useFlowContext } from '@cash-web/shared-flow-data-access-context'

import { getSetSignatureErrors } from '../../../util/statusErrors'
import { BaseBlocker } from '../../BaseBlocker/BaseBlocker'
import Caveat from './Caveat.ttf'
import DancingScript from './DancingScript.ttf'
import { EditFormName } from './EditFormName'
import Kalam from './Kalam.ttf'

type Font = {
  name: string
  fontSizeSlope: number
}

const fonts: Font[] = [
  { name: 'Dancing Script', fontSizeSlope: 1.48 },
  { name: 'Caveat', fontSizeSlope: 1.4 },
  { name: 'Kalam', fontSizeSlope: 1.6 },
]

const fontSizeByLengthAndFont = (name: string, slope: number) => {
  // equation to normalize font size based on length of name
  return `${(100 - name.length * slope) / 1.3}px`
}

const SignatureUnderscore = styled.div`
  width: 90%;
  position: absolute;
  border-bottom: 0.125rem solid ${props => props.theme.colors.placeholderBackground};
  bottom: 1.875rem;
  left: 1.25rem;
`
const CanvasWrapper = styled(Flex)`
  width: 100%;
  height: 9rem;
  padding-left: 3.25rem;
  padding-top: ${({ theme }) => theme.spacing.s};
  position: relative;
  background: ${props => props.theme.colors.primaryButtonTint};
  color: ${props => props.theme.colors.placeholderBackground};
  border-radius: 0.75rem;
  border: ${props =>
    props.theme.darkMode
      ? `0.125rem solid ${props.theme.colors.primaryButtonTint} `
      : `0.125rem solid  ${props.theme.colors.nestedCardOutline}`};
`

const Canvas = styled.canvas`
  background: transparent;
  z-index: 10;
  width: 100%;
  height: 7.5rem;
  display: block;
  object-fit: contain;
`
const drawSignature = ({ fullName, selectedFont }: { fullName: string; selectedFont: Font }) => {
  const canvas = document.getElementById('signatureCanvas') as HTMLCanvasElement
  const ctx = canvas.getContext('2d')

  if (ctx && fullName) {
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    const selectedFontStyle = `${fontSizeByLengthAndFont(fullName, selectedFont.fontSizeSlope)} ${selectedFont.name}`
    ctx.font = selectedFontStyle
    ctx.fillText(fullName, 18, 160)
  }
}

export const BlockerSignature = ({ blocker }: { blocker: SignatureBlocker }) => {
  const { profile } = useAuth()
  const { t } = useTranslation()

  const flow = useFlowContext()
  const { handleBlockerResponse } = useBlockerResponseHandler()
  const [selectedFontIndex, setSelectedFontIndex] = useState(0)
  const [isFontsLoaded, setIsFontsLoaded] = useState(false)
  const [userInputtedName, setUserInputtedName] = useState(blocker?.prefill_customer_name || '')
  const [isNameInputView, setIsNameInputView] = useState(false)

  const handleChangeFont = () => {
    const index = (selectedFontIndex + 1) % fonts.length
    setSelectedFontIndex(index)
  }

  const { mutateAsync: mutateSignature } = useMutationSetSignature({
    options: {
      flowToken: flow.active?.flowToken,
      clientScenario: flow.active?.clientScenario,
    },
  })

  const form = useForm()

  useEffect(() => {
    if (!document.fonts) return
    const dancingScriptFont = new FontFace('Dancing Script', `url(${DancingScript})`)
    const caveatFont = new FontFace('Caveat', `url(${Caveat})`)
    const kalamFont = new FontFace('Kalam', `url(${Kalam})`)
    Promise.all([dancingScriptFont.load(), caveatFont.load(), kalamFont.load()]).then(() => {
      document.fonts.add(dancingScriptFont)
      document.fonts.add(caveatFont)
      document.fonts.add(kalamFont)
      setIsFontsLoaded(true)
    })
  }, [])

  useEffect(() => {
    if (!isFontsLoaded || isNameInputView) return

    drawSignature({
      fullName: userInputtedName,
      selectedFont: fonts[selectedFontIndex],
    })
  }, [isNameInputView, isFontsLoaded, profile, selectedFontIndex, userInputtedName])

  const onSubmit = async () => {
    const canvas = document.getElementById('signatureCanvas') as HTMLCanvasElement
    const dataUrl = canvas.toDataURL('image/png')
    if (!dataUrl) return
    const binaryString = dataUrl.split(',')[1]
    await handleBlockerResponse({
      promisedBlockerUpdate: mutateSignature({ image_bytes: binaryString, mime_type: 'image/png' }),
      errors: getSetSignatureErrors(t),
      isResponseSuccess: response => {
        const blockerDescriptors = response.response_context?.scenario_plan?.blocker_descriptors
        return !!blockerDescriptors?.length && blockerDescriptors?.length > 0
      },
    })
  }

  const handleBackToCanvasView = (shouldClearUserInput = false) => {
    if (shouldClearUserInput) {
      setUserInputtedName(blocker?.prefill_customer_name || '')
    }
    setIsNameInputView(false)
  }

  return isNameInputView ? (
    <EditFormName
      handleBackToCanvasView={handleBackToCanvasView}
      setUserInputtedName={setUserInputtedName}
      userInputtedName={userInputtedName}
    />
  ) : (
    <BaseBlocker
      mainText={t('Choose your signature', { context: 'Title text.' })}
      secondaryText={t('This will be your signature on the form.', { context: 'subtitle text.' })}
      onSubmit={onSubmit}
      form={form}
    >
      <CanvasWrapper flexDirection="column">
        <Canvas id="signatureCanvas" width="630px" height="216px"></Canvas>
        <SignatureUnderscore>
          <PaddingInset>
            <NavigationCloseIcon />
          </PaddingInset>
        </SignatureUnderscore>
      </CanvasWrapper>
      <Flex justifyContent="space-between" alignItems="center" gap={spacing.m}>
        <Button size="large" variant="tertiary" onClick={handleChangeFont}>
          {t('Change font', { context: 'button text' })}
        </Button>
        <Button size="large" variant="tertiary" onClick={() => setIsNameInputView(true)}>
          {t('Edit name', { context: 'text button for switching fonts' })}
        </Button>
      </Flex>
    </BaseBlocker>
  )
}
